import { useQuery, useReactiveVar } from '@apollo/client'
import { GetProjectNoteContentItemIdsQuery } from 'api/graphQL/queries/project.queries'
import {
    setElementLevelNotesCount,
    setElementNotesCount,
    workspaceSelectedElement,
} from 'api/graphQL/reactiveVars/element'
import {
    TASK_STATE_KEY,
    syncTaskContentItemIds,
} from 'api/graphQL/reactiveVars/workspaceTasks'
import { ProjectElementNodeWithMetadata } from 'api/graphQL/types/projectElement.types'
import { useEffect } from 'react'
import { usePreviewFeatures } from 'shared/hooks/usePreviewFeatures'

interface UseProjectTasksSyncOptions {
    projectId?: string
    selectedProjectNode?: ProjectElementNodeWithMetadata | null
}

interface UseProjectTasksSyncResult {
    isProjectTasksEnabled: boolean
    taskCount: number
}

/** Syncs the task-indicator content-item IDs and exposes the badge count for the Tasks button. */
export const useProjectTasksSync = ({
    projectId,
    selectedProjectNode,
}: UseProjectTasksSyncOptions): UseProjectTasksSyncResult => {
    const { previewFeatures } = usePreviewFeatures()
    const isProjectTasksEnabled = previewFeatures.hasProjectTasks
    const taskCount = useReactiveVar(workspaceSelectedElement).elementNotesCount
    const selectedElementId = selectedProjectNode?.elementId
    const isInProject = selectedProjectNode?.isInProject

    // Fetches note IDs/count for the selected element.
    // In-project: filtered by elementId so indicators and count are element-scoped.
    // Not-in-project: no element filter so the badge shows the project-total count (bug 2844093).
    const { data: noteCountData, variables: noteCountVariables } = useQuery<{
        getProject: { projectNotes: { id: string; contentItemId?: string }[] }
    }>(GetProjectNoteContentItemIdsQuery, {
        variables: {
            projectId,
            projectNoteSearchInput: {
                filter: isInProject && selectedElementId
                    ? { elementId: selectedElementId }
                    : {},
                limit: 500,
                skip: 0,
            },
        },
        skip: !isProjectTasksEnabled || !projectId || (!!isInProject && !selectedElementId),
        fetchPolicy: 'network-only',
    })

    useEffect(() => {
        if (!noteCountData) return
        const notes = noteCountData.getProject?.projectNotes ?? []
        // Branch from the variables that produced this response — never from a live ref.
        // This is race-free: data and the scoping decision come from the same request.
        const wasElementScoped = !!noteCountVariables?.projectNoteSearchInput?.filter?.elementId
        if (wasElementScoped) {
            syncTaskContentItemIds(notes)
            setElementNotesCount(notes.length)
            setElementLevelNotesCount(notes.filter((n) => !n.contentItemId).length)
        } else {
            // Not-in-project: show project-total count; no element-level indicator dots.
            setElementNotesCount(notes.length)
            setElementLevelNotesCount(0)
        }
    }, [noteCountData, noteCountVariables])

    // Clear stale content-item indicators when the selected element is not in the project.
    // Count is intentionally left for the query above to set — it fires immediately on
    // isInProject change (variable change triggers re-query) so zeroing here would race.
    useEffect(() => {
        if (!isInProject) {
            syncTaskContentItemIds([])
        }
    }, [isInProject])

    // After the task window performs any CRUD operation it broadcasts the current notes
    // state via localStorage. Only apply the broadcast when it is scoped to the current
    // element to prevent cross-element indicator bleed (bug 2841505):
    //   in-project  → payload.elementId must match selectedElementId
    //   not-in-project → dialog opens with no element param, broadcasts elementId=''
    useEffect(() => {
        if (!isProjectTasksEnabled || !projectId) return
        const onStorage = (e: StorageEvent) => {
            if (e.key !== TASK_STATE_KEY) return
            try {
                const payload = e.newValue ? JSON.parse(e.newValue) : null
                if (payload?.projectId !== projectId) return
                const elementMatches = isInProject
                    ? payload.elementId === selectedElementId
                    : payload.elementId === ''
                if (!elementMatches) return
                const ids: string[] = Array.isArray(payload.contentItemIds)
                    ? payload.contentItemIds
                    : []
                if (isInProject) {
                    syncTaskContentItemIds(ids.map((id) => ({ contentItemId: id })))
                }
                setElementNotesCount(Number(payload.totalCount ?? 0))
                setElementLevelNotesCount(Number(payload.elementLevelCount ?? 0))
            } catch {
                // ignore malformed payload
            }
        }
        window.addEventListener('storage', onStorage)
        return () => window.removeEventListener('storage', onStorage)
    }, [isProjectTasksEnabled, projectId, isInProject, selectedElementId])

    return { isProjectTasksEnabled, taskCount }
}
